import type { TFunction } from 'i18next';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  listCards,
  listVehicles,
  searchSuppliers,
  type ApiOutcome,
  type Card,
  type Supplier,
  type Vehicle,
} from './api';
import type { MethodPick } from './form-model';
import { NativeSelect } from './NativeSelect';

type List<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly T[] }
  | { readonly status: 'unavailable' };

/** One of the owner's lists, loaded again when the owner changes. */
function useOwnerList<T>(
  load: (owner?: string) => Promise<ApiOutcome<readonly T[]>>,
  owner: string | undefined,
): List<T> {
  const [list, setList] = useState<List<T>>({ status: 'loading' });
  useEffect(() => {
    let active = true;
    setList({ status: 'loading' });
    void load(owner).then((outcome) => {
      if (!active) return;
      setList(outcome.ok ? { status: 'ready', items: outcome.value } : { status: 'unavailable' });
    });
    return () => {
      active = false;
    };
  }, [load, owner]);
  return list;
}

interface Choice {
  readonly id: string;
  readonly label: string;
}

function hintFor(
  t: TFunction<'expenses'>,
  status: List<unknown>['status'],
  choiceCount: number,
  emptyText: string,
): string | null {
  if (status === 'loading') return t('capture.references.loading');
  if (status === 'unavailable') return t('capture.references.unavailable');
  return choiceCount === 0 ? emptyText : null;
}

/**
 * A select of the owner's list with «Non in elenco» first (always allowed). A stored choice the list
 * no longer holds (or a list that did not load) still shows, under its stored label.
 */
function OwnerListSelect({
  fieldKey,
  list,
  choices,
  value,
  storedLabel,
  emptyText,
  labelText,
  onChange,
}: {
  readonly fieldKey: 'cardId' | 'vehicleId';
  /** Overrides the field's own label («Conto» for a transfer's account). */
  readonly labelText?: string;
  readonly list: List<unknown>;
  readonly choices: readonly Choice[];
  readonly value: string;
  readonly storedLabel: string;
  readonly emptyText: string;
  readonly onChange: (id: string, label: string) => void;
}) {
  const { t } = useTranslation('expenses');
  const uid = useId();
  const options =
    value !== '' && !choices.some((c) => c.id === value)
      ? [...choices, { id: value, label: storedLabel || value }]
      : choices;
  const hint = hintFor(t, list.status, choices.length, emptyText);
  return (
    <div className="min-w-0 space-y-1" data-field={fieldKey}>
      <label htmlFor={`${uid}-${fieldKey}`} className="text-sm font-medium">
        {labelText ?? t(`capture.fields.${fieldKey}`)}
      </label>
      <NativeSelect
        id={`${uid}-${fieldKey}`}
        value={value}
        aria-describedby={hint ? `${uid}-${fieldKey}-hint` : undefined}
        onChange={(e) => {
          const id = e.target.value;
          onChange(id, options.find((o) => o.id === id)?.label ?? '');
        }}
      >
        <option value="">{t('capture.references.notListed')}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </NativeSelect>
      {hint && (
        <p id={`${uid}-${fieldKey}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * «Carta» or «Conto»: the owner's payment methods of the class and scope «Come hai pagato?» names
 * (decision 153). nexpense's transfers, cash and other entries are never offered as a card.
 */
export function CardPicker({
  owner,
  pick,
  value,
  storedLabel,
  onChange,
}: {
  /** The expense's owner; undefined for the caller. */
  readonly owner: string | undefined;
  readonly pick: MethodPick;
  readonly value: string;
  readonly storedLabel: string;
  readonly onChange: (id: string, label: string) => void;
}) {
  const { t } = useTranslation('expenses');
  const list = useOwnerList<Card>(listCards, owner);
  const transfer = pick.methodClass === 'transfer';
  const choices =
    list.status === 'ready'
      ? list.items
          .filter(
            (card) =>
              card.methodClass === pick.methodClass &&
              (pick.scope === 'any' || card.kind === pick.scope),
          )
          .map((card) => ({ id: card.id, label: card.label }))
      : [];
  return (
    <OwnerListSelect
      fieldKey="cardId"
      list={list}
      choices={choices}
      value={value}
      storedLabel={storedLabel}
      emptyText={t(transfer ? 'capture.references.emptyAccounts' : 'capture.references.emptyCards')}
      {...(transfer ? { labelText: t('capture.fields.accountId') } : {})}
      onChange={onChange}
    />
  );
}

/** «Veicolo»: the owner's vehicles, for mileage and fuel. */
export function VehiclePicker({
  owner,
  value,
  storedLabel,
  onChange,
}: {
  readonly owner: string | undefined;
  readonly value: string;
  readonly storedLabel: string;
  readonly onChange: (id: string, label: string) => void;
}) {
  const { t } = useTranslation('expenses');
  const list = useOwnerList<Vehicle>(listVehicles, owner);
  const choices =
    list.status === 'ready'
      ? list.items.map((vehicle) => ({
          id: vehicle.id,
          label: vehicle.plate
            ? t('capture.references.plate', { label: vehicle.label, plate: vehicle.plate })
            : vehicle.label,
        }))
      : [];
  return (
    <OwnerListSelect
      fieldKey="vehicleId"
      list={list}
      choices={choices}
      value={value}
      storedLabel={storedLabel}
      emptyText={t('capture.references.emptyVehicles')}
      onChange={onChange}
    />
  );
}

export const SUPPLIER_SEARCH_DELAY_MS = 250;
const SUPPLIER_MIN_CHARS = 2;

/** The supplier search as the operator types: suggestions, the active one, and picking. */
function useSupplierSearch(onPick: (supplier: Supplier) => void) {
  const [query, setQuery] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<readonly Supplier[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const run = useRef(0);

  useEffect(() => {
    const text = query?.trim() ?? '';
    const current = ++run.current;
    if (text.length < SUPPLIER_MIN_CHARS) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      void searchSuppliers(text).then((outcome) => {
        if (current !== run.current) return;
        setSuggestions(outcome.ok ? outcome.value : []);
        setActive(-1);
      });
    }, SUPPLIER_SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const shown = open && suggestions.length > 0;
  const pick = (supplier: Supplier): void => {
    run.current += 1;
    setQuery(null);
    setSuggestions([]);
    setOpen(false);
    onPick(supplier);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (!shown) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      pick(suggestions[active]!);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };
  return { setQuery, suggestions, shown, setOpen, active, pick, onKeyDown };
}

function SuggestionList({
  listId,
  suggestions,
  shown,
  active,
  onPick,
}: {
  readonly listId: string;
  readonly suggestions: readonly Supplier[];
  readonly shown: boolean;
  readonly active: number;
  readonly onPick: (supplier: Supplier) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <ul
      id={listId}
      role="listbox"
      aria-label={t('capture.references.suggestions')}
      hidden={!shown}
      className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-popover p-1 text-sm shadow-md"
    >
      {suggestions.map((supplier, index) => (
        <li
          key={supplier.id}
          id={`${listId}-${index}`}
          role="option"
          aria-selected={index === active}
          className={cn(
            'cursor-pointer rounded px-2 py-1.5',
            index === active && 'bg-selected text-selected-foreground',
          )}
          // Keep the focus in the field so its blur does not close the list before the click.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(supplier)}
        >
          <span className="font-medium">{supplier.name}</span>
          {supplier.vat && (
            <span className="ml-2 text-xs text-muted-foreground">
              {t('capture.references.supplierVat', { vat: supplier.vat })}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * The supplier's name with suggestions from the supplier list: after two typed characters and a
 * short pause it searches; picking one fills name and VAT number. Free text is always allowed, and
 * only what the person types searches (a name the receipt proposed does not).
 */
export function SupplierNameInput({
  id,
  value,
  picked,
  onType,
  onPick,
  onBlur,
  describedBy,
  invalid,
}: {
  readonly id: string;
  readonly value: string;
  /** Whether the value came from the list. */
  readonly picked: boolean;
  readonly onType: (text: string) => void;
  readonly onPick: (supplier: Supplier) => void;
  readonly onBlur?: () => void;
  readonly describedBy?: string | undefined;
  readonly invalid?: boolean | undefined;
}) {
  const { t } = useTranslation('expenses');
  const listId = `${id}-suggestions`;
  const { setQuery, suggestions, shown, setOpen, active, pick, onKeyDown } =
    useSupplierSearch(onPick);

  return (
    <div className="relative">
      <Input
        id={id}
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={shown}
        aria-controls={listId}
        aria-activedescendant={shown && active >= 0 ? `${listId}-${active}` : undefined}
        aria-describedby={
          [describedBy, picked ? `${id}-picked` : undefined].filter(Boolean).join(' ') || undefined
        }
        aria-invalid={invalid || undefined}
        value={value}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          onType(e.target.value);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          setOpen(false);
          onBlur?.();
        }}
      />
      <SuggestionList
        listId={listId}
        suggestions={suggestions}
        shown={shown}
        active={active}
        onPick={pick}
      />
      {picked && (
        <p id={`${id}-picked`} className="mt-1 text-xs text-muted-foreground">
          {t('capture.references.supplierPicked')}
        </p>
      )}
    </div>
  );
}
